import { useMemo, useState } from 'react'
import { BookOpenCheck, Compass, Search, ShieldCheck } from 'lucide-react'
import { ArticleCard } from '../components/ArticleCard'
import { PageHero } from '../components/PageHero'
import { PageMeta } from '../components/PageMeta'
import { Reveal } from '../components/Reveal'
import { blogPosts } from '../data/blogs'

const categories = ['All', 'Prevention', 'Healthy living', 'Medicines', 'Mental wellbeing'] as const

export default function InsightsPage() {
  const [category, setCategory] = useState<(typeof categories)[number]>('All')
  const [query, setQuery] = useState('')
  const filtered = useMemo(() => blogPosts
    .filter((post) => {
      const matchesCategory = category === 'All' || post.category === category
      const haystack = `${post.title} ${post.excerpt} ${post.category} ${post.sections.map((section) => `${section.heading} ${section.paragraphs.join(' ')} ${(section.bullets ?? []).join(' ')}`).join(' ')}`.toLowerCase()
      return matchesCategory && haystack.includes(query.toLowerCase())
    })
    .sort((a, b) => b.published.localeCompare(a.published) || a.title.localeCompare(b.title)), [category, query])

  return (
    <>
      <PageMeta title="Health insights" description={`Explore ${blogPosts.length} source-linked, patient-friendly guides on prevention, healthy living, medicines, mental wellbeing, warning signs, and everyday health.`} image="/images/health-insights-library.webp" imageAlt="Colorful editorial health library with an open book, walking path, heart rhythm, food, water, sleep and shield motifs" />
      <PageHero
        eyebrow="The health edit"
        title={<>Reliable information for <span className="serif-accent">real-life decisions.</span></>}
        copy={`${blogPosts.length} concise, source-linked guides designed to inform your next question—not diagnose your next symptom.`}
        aside={<div className="insight-count"><strong>{blogPosts.length}</strong><span>patient-friendly guides</span><small>Official and authoritative sources</small></div>}
      />

      <section className="insights-art-section">
        <div className="container insights-art-panel">
          <Reveal className="insights-art-figure">
            <img src="/images/health-insights-library.webp" width="2055" height="765" alt="Colorful editorial health library with an open book, walking path, heart rhythm, food, water, sleep and shield motifs" />
            <span>AI-assisted concept art · health guidance remains source-linked</span>
          </Reveal>
          <Reveal className="insights-art-copy" delay={0.08}>
            <span className="eyebrow"><Compass size={15} /> Find your next useful question</span>
            <h2>Built for reading, remembering, and <span className="serif-accent">taking action.</span></h2>
            <p>Each guide starts with plain language, highlights practical patterns, and ends with a direct source. Search for a symptom, habit, medicine, or question—or browse by category.</p>
            <div className="insight-trust-row">
              <span><BookOpenCheck size={18} /><b>Complete explainers</b>Not headline fragments</span>
              <span><ShieldCheck size={18} /><b>Clear boundaries</b>Urgent signs stay visible</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section insights-index-section">
        <div className="container">
          <div className="filter-bar">
            <div className="category-filters" aria-label="Filter articles by category">
              {categories.map((item) => <button className={category === item ? 'is-active' : ''} onClick={() => setCategory(item)} key={item}>{item}</button>)}
            </div>
            <label className="search-field"><Search size={18} /><span className="sr-only">Search health guides</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search guides" /></label>
          </div>
          <p className="result-count">Showing {filtered.length} {filtered.length === 1 ? 'guide' : 'guides'}</p>
          {filtered.length ? (
            <div className="article-grid insights-grid">
              {filtered.map((post, index) => <Reveal key={post.slug} delay={(index % 3) * 0.04}><ArticleCard post={post} featured={post.featured} /></Reveal>)}
            </div>
          ) : (
            <div className="empty-state"><Search size={28} /><h2>No guide found</h2><p>Try a different word or reset the category filter.</p><button className="button button-ghost" onClick={() => { setQuery(''); setCategory('All') }}>Reset filters</button></div>
          )}
        </div>
      </section>

      <section className="container content-disclaimer">
        <strong>Use these guides as a starting point.</strong>
        <p>They are educational only and cannot account for your history, examination, tests, medicines, pregnancy, allergies, or local clinical guidance.</p>
      </section>
    </>
  )
}
